import { useState } from "react";
import type { Lead, LeadFollowUp } from "@/types/lead";
import { followUpFromLocalDateTimeInput, followUpToLocalDateTimeInput } from "@/lib/lead/followUpDateTimeLocal";
import { cardTitleTextClass, compactInputClass, insetSurfaceCardClass } from "@/components/common/surfaceClasses";

type Props = {
  lead: Lead;
  followUps: LeadFollowUp[];
  onCreateFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
  onSaveFollowUp: (
    leadId: string,
    followUpId: string,
    patch: Partial<{ next_action: string; due_date: string; reason: string; status: LeadFollowUp["status"] }>
  ) => void;
};

export function SalesConsoleCustomerCalendarEventsPanel(props: Props) {
  const [newAction, setNewAction] = useState("");
  const [newDue, setNewDue] = useState("");
  const [newReason, setNewReason] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<{ next_action: string; due_date: string; reason: string; status: LeadFollowUp["status"] } | null>(null);

  return (
    <>
      <div className={`${insetSurfaceCardClass} border-indigo-100 dark:border-indigo-800/60`}>
        <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">Create event</div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input
            className={compactInputClass}
            placeholder="Next action (e.g. Call after permit review)"
            value={newAction}
            onChange={(e) => setNewAction(e.target.value)}
          />
          <input className={compactInputClass} type="datetime-local" value={newDue} onChange={(e) => setNewDue(e.target.value)} />
          <input
            className={`sm:col-span-2 ${compactInputClass}`}
            placeholder="Reason / context"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          disabled={!newAction.trim() || !newDue}
          onClick={() => {
            props.onCreateFollowUp(props.lead.id, {
              next_action: newAction,
              due_date: followUpFromLocalDateTimeInput(newDue),
              reason: newReason,
            });
            setNewAction("");
            setNewDue("");
            setNewReason("");
          }}
        >
          Add calendar event
        </button>
      </div>

      <div className="space-y-2">
        {props.followUps.map((f) => {
          const isEditing = editingId === f.id && editingDraft != null;
          const d = editingDraft;
          return (
            <div key={f.id} className={insetSurfaceCardClass}>
              {isEditing && d ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className={compactInputClass}
                    value={d.next_action}
                    onChange={(e) => setEditingDraft({ ...d, next_action: e.target.value })}
                  />
                  <input
                    className={compactInputClass}
                    type="datetime-local"
                    value={followUpToLocalDateTimeInput(d.due_date)}
                    onChange={(e) => setEditingDraft({ ...d, due_date: followUpFromLocalDateTimeInput(e.target.value) })}
                  />
                  <input
                    className={`sm:col-span-2 ${compactInputClass}`}
                    value={d.reason}
                    onChange={(e) => setEditingDraft({ ...d, reason: e.target.value })}
                  />
                  <select
                    className={compactInputClass}
                    value={d.status}
                    onChange={(e) => setEditingDraft({ ...d, status: e.target.value as LeadFollowUp["status"] })}
                  >
                    <option value="open">Open</option>
                    <option value="done">Done</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <div className="flex items-center gap-3 text-xs">
                    <button
                      type="button"
                      className="text-emerald-700 underline dark:text-emerald-400"
                      onClick={() => {
                        props.onSaveFollowUp(props.lead.id, f.id, d);
                        setEditingId(null);
                        setEditingDraft(null);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="text-gray-600 underline dark:text-zinc-400"
                      onClick={() => {
                        setEditingId(null);
                        setEditingDraft(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={`font-medium text-sm ${cardTitleTextClass}`}>{f.next_action}</div>
                  <div className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
                    {new Date(f.due_date).toLocaleString()} · {f.status}
                    {f.reason ? ` · ${f.reason}` : ""}
                  </div>
                  <button
                    type="button"
                    className="mt-1 text-xs text-blue-700 underline dark:text-blue-400"
                    onClick={() => {
                      setEditingId(f.id);
                      setEditingDraft({ next_action: f.next_action, due_date: f.due_date, reason: f.reason, status: f.status });
                    }}
                  >
                    Edit event
                  </button>
                </>
              )}
            </div>
          );
        })}
        {props.followUps.length === 0 ? <p className="text-xs text-gray-500 dark:text-zinc-400">No events for this lead yet.</p> : null}
      </div>
    </>
  );
}
